import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, Upload, Eye, Trash2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const DOC_TYPE_LABELS = { license_disc: 'License Disc', registration_disc: 'Registration Disc', other: 'Other' };

const VehicleDocumentsTab = ({ vehicleId, documents, setDocuments, onRefresh }) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDocDialog, setDeleteDocDialog] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ doc_type: 'license_disc', doc_name: '', expiry_date: '', file: null });
  const fileInputRef = useRef(null);

  const isExpired = (date) => date && new Date(date) < new Date();
  const isExpiringSoon = (date) => {
    if (!date) return false;
    const d = new Date(date);
    const now = new Date();
    return d > now && d - now < 30 * 86400000;
  };

  const handleUploadDocument = async () => {
    if (!uploadForm.file || !uploadForm.doc_name) { toast.error('File and name are required'); return; }
    setUploading(true);
    try {
      await api.uploadVehicleDocument(vehicleId, uploadForm);
      toast.success('Document uploaded');
      setUploadDialogOpen(false);
      setUploadForm({ doc_type: 'license_disc', doc_name: '', expiry_date: '', file: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      onRefresh();
    } catch {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!selectedDoc) return;
    try {
      await api.deleteVehicleDocument(vehicleId, selectedDoc.id);
      toast.success('Document deleted');
      setDocuments(prev => prev.filter(d => d.id !== selectedDoc.id));
      setDeleteDocDialog(false);
    } catch {
      toast.error('Failed to delete document');
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-heading font-semibold">Vehicle Documents</CardTitle>
          <Button onClick={() => setUploadDialogOpen(true)} className="bg-gray-800 hover:bg-gray-700" data-testid="upload-doc-btn">
            <Upload className="w-4 h-4 mr-2" />Upload Document
          </Button>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No documents uploaded yet</p>
              <p className="text-sm mt-1">Upload License Disc, Registration Disc or other documents</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map(doc => {
                const expired = isExpired(doc.expiry_date);
                const expiring = isExpiringSoon(doc.expiry_date);
                return (
                  <div key={doc.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-md hover:bg-slate-100" data-testid={`doc-${doc.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-md flex items-center justify-center">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{doc.doc_name}</span>
                          <Badge variant="secondary" className="text-xs">{DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}</Badge>
                        </div>
                        <div className="text-sm text-slate-500 mt-0.5 flex items-center gap-2">
                          <span>Uploaded {doc.uploaded_at?.slice(0, 10)}</span>
                          {doc.expiry_date && (
                            <span className={`flex items-center gap-1 font-medium ${expired ? 'text-red-600' : expiring ? 'text-amber-600' : 'text-green-600'}`}>
                              <Calendar className="w-3 h-3" />
                              Expires: {doc.expiry_date}
                              {expired && ' (EXPIRED)'}
                              {!expired && expiring && ' (soon)'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noopener noreferrer" data-testid={`view-doc-${doc.id}`}>
                        <Button variant="ghost" size="icon" title="View"><Eye className="w-4 h-4 text-blue-500" /></Button>
                      </a>
                      <Button variant="ghost" size="icon" onClick={() => { setSelectedDoc(doc); setDeleteDocDialog(true); }} data-testid={`delete-doc-${doc.id}`} title="Delete"><Trash2 className="w-4 h-4 text-slate-400" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Document</DialogTitle>
            <DialogDescription>Upload a vehicle document (PDF, JPG, or PNG, max 10MB)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Document Type *</Label>
                <Select value={uploadForm.doc_type} onValueChange={(v) => setUploadForm(f => ({ ...f, doc_type: v }))}>
                  <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="license_disc">License Disc</SelectItem>
                    <SelectItem value="registration_disc">Registration Disc</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Document Name *</Label>
                <Input value={uploadForm.doc_name} onChange={(e) => setUploadForm(f => ({ ...f, doc_name: e.target.value }))} placeholder="e.g., License 2025" data-testid="doc-name-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Expiry Date</Label>
              <Input type="date" value={uploadForm.expiry_date} onChange={(e) => setUploadForm(f => ({ ...f, expiry_date: e.target.value }))} data-testid="doc-expiry-input" />
            </div>
            <div className="space-y-2">
              <Label>File *</Label>
              <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 cursor-pointer" onChange={(e) => setUploadForm(f => ({ ...f, file: e.target.files[0] || null }))} data-testid="doc-file-input" />
              {uploadForm.file && <p className="text-xs text-gray-500">{uploadForm.file.name} ({(uploadForm.file.size / 1024).toFixed(0)} KB)</p>}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUploadDocument} disabled={uploading || !uploadForm.file || !uploadForm.doc_name} className="bg-gray-800 hover:bg-gray-700" data-testid="confirm-upload-btn">
                <Upload className="w-4 h-4 mr-2" />{uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Document */}
      <AlertDialog open={deleteDocDialog} onOpenChange={setDeleteDocDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete &quot;{selectedDoc?.doc_name}&quot;. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDocument} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default VehicleDocumentsTab;
